import React, { useState, useEffect } from 'react';
import {
  Card,
  Form,
  Input,
  Switch,
  Button,
  Space,
  Divider,
  Typography,
  Alert,
  Spin,
  Row,
  Col,
  Tooltip,
} from 'antd';
import {
  ApiOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  RocketOutlined,
} from '@ant-design/icons';
import ClickUpConfigApi from '../../api/settings/ClickUpConfigApi';

const { Title, Text } = Typography;
const { Password } = Input;

const ClickUpConfig = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [testLoading, setTestLoading] = useState(false);
  const [config, setConfig] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  // Load existing configuration on component mount
  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    setLoading(true);
    try {
      const existingConfig = await ClickUpConfigApi.getConfig();
      if (existingConfig) {
        setConfig(existingConfig);
        // Set form values with masked API key
        form.setFieldsValue({
          enabled: existingConfig.enabled,
          apiKey: '', // Don't populate masked API key
          defaultTeamId: existingConfig.defaultTeamId,
          defaultSpaceId: existingConfig.defaultSpaceId,
        });
      } else {
        // No config exists, start in editing mode
        setIsEditing(true);
        form.setFieldsValue({
          enabled: false,
          apiKey: '',
          defaultTeamId: '',
          defaultSpaceId: '',
        });
      }
    } catch (error) {
      console.error('Failed to load ClickUp config:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (values) => {
    setLoading(true);
    try {
      // Only include fields that have values (don't send empty API key)
      const configData = {
        enabled: Boolean(values.enabled), // Ensure boolean conversion
        apiKey: values.apiKey || undefined,
        defaultTeamId: values.defaultTeamId || undefined,
        defaultSpaceId: values.defaultSpaceId || undefined,
      };

      let result;
      if (config) {
        // Update existing config
        result = await ClickUpConfigApi.updateConfig(configData);
      } else {
        // Create new config
        result = await ClickUpConfigApi.createConfig(configData);
      }

      setConfig(result);
      setIsEditing(false);
      
      // Reload to get the latest masked values
      await loadConfig();
    } catch (error) {
      console.error('Failed to save ClickUp config:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTestConnection = async () => {
    try {
      const values = await form.validateFields(['apiKey']);
      setTestLoading(true);

      const testData = {
        apiKey: values.apiKey || config?.apiKey,
        teamId: values.defaultTeamId || config?.defaultTeamId,
      };

      await ClickUpConfigApi.testConnection(testData);
    } catch (error) {
      console.error('Connection test failed:', error);
    } finally {
      setTestLoading(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    // Reset form to original values
    if (config) {
      form.setFieldsValue({
        enabled: config.enabled,
        apiKey: '',
        defaultTeamId: config.defaultTeamId,
        defaultSpaceId: config.defaultSpaceId,
      });
    }
  };

  if (loading && !config) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Spin size="large" />
          <div style={{ marginTop: '16px' }}>Loading ClickUp configuration...</div>
        </div>
      </Card>
    );
  }

  return (
    <div style={{ padding: '24px', height: '100vh', overflow: 'auto' }}>
      <Card
        title={
          <Space>
            <RocketOutlined />
            <span>ClickUp Integration Configuration</span>
          </Space>
        }
        extra={
          !isEditing && (
            <Button type="primary" onClick={() => setIsEditing(true)}>
              Edit Configuration
            </Button>
          )
        }
      >
        <Alert
          message="ClickUp Integration Settings"
          description="Configure the connection to your ClickUp workspace for seamless task synchronization between Strategizer and ClickUp."
          type="info"
          showIcon
          style={{ marginBottom: '24px' }}
        />

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSave}
          disabled={!isEditing}
        >
          <Row gutter={[16, 0]}>
            <Col span={24}>
              <Form.Item
                name="enabled"
                valuePropName="checked"
                initialValue={false}
              >
                <Space>
                  <Switch 
                    onChange={(checked) => {
                      form.setFieldsValue({ enabled: checked });
                    }}
                  />
                  <Text strong>Enable ClickUp Integration</Text>
                  <Tooltip title="Toggle to enable or disable ClickUp integration system-wide">
                    <ExclamationCircleOutlined style={{ color: '#1890ff' }} />
                  </Tooltip>
                </Space>
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left">
            <Space>
              <ApiOutlined />
              <span>API Configuration</span>
            </Space>
          </Divider>

          <Row gutter={[16, 0]}>
            <Col span={24}>
              <Form.Item
                name="apiKey"
                label={
                  <Space>
                    <span>ClickUp API Key</span>
                    {config && config.apiKeyMasked && (
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        (Current: {config.apiKeyMasked})
                      </Text>
                    )}
                  </Space>
                }
                rules={[
                  { required: !config, message: 'Please enter the ClickUp API key' },
                ]}
                extra="Get your API key from ClickUp Settings > Apps > API"
              >
                <Password
                  placeholder={config ? "Leave empty to keep current key" : "Enter ClickUp API key (pk_...)"}
                  iconRender={(visible) => (visible ? <EyeOutlined /> : <EyeInvisibleOutlined />)}
                />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left">
            <Space>
              <RocketOutlined />
              <span>Default Settings (Optional)</span>
            </Space>
          </Divider>

          <Row gutter={[16, 0]}>
            <Col span={12}>
              <Form.Item
                name="defaultTeamId"
                label="Default Team ID"
                extra="Optional: Set a default ClickUp team for new tasks"
              >
                <Input
                  placeholder="Enter team ID (optional)"
                  prefix={<RocketOutlined />}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="defaultSpaceId"
                label="Default Space ID"
                extra="Optional: Set a default ClickUp space for new tasks"
              >
                <Input
                  placeholder="Enter space ID (optional)"
                  prefix={<RocketOutlined />}
                />
              </Form.Item>
            </Col>
          </Row>

          {isEditing && (
            <>
              <Divider />
              <Row gutter={[16, 0]} justify="space-between">
                <Col>
                  <Button
                    icon={<CheckCircleOutlined />}
                    onClick={handleTestConnection}
                    loading={testLoading}
                    disabled={loading}
                  >
                    Test Connection
                  </Button>
                </Col>
                <Col>
                  <Space>
                    <Button onClick={handleCancel} disabled={loading}>
                      Cancel
                    </Button>
                    <Button
                      type="primary"
                      htmlType="submit"
                      loading={loading}
                    >
                      Apply Configuration
                    </Button>
                  </Space>
                </Col>
              </Row>
            </>
          )}
        </Form>

        {config && !isEditing && (
          <>
            <Divider />
            <Alert
              message="Configuration Status"
              description={
                config.enabled
                  ? "ClickUp integration is enabled and configured. Tasks can now be synced to ClickUp."
                  : "ClickUp integration is configured but currently disabled."
              }
              type={config.enabled ? "success" : "warning"}
              showIcon
            />
          </>
        )}
      </Card>
    </div>
  );
};

export default ClickUpConfig;